import { isPlatformServer } from '@angular/common';
import { inject, PLATFORM_ID, RESPONSE_INIT } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { SeoService } from '@shared/services/seo.service';
import { BootFallbackService } from './boot-fallback.service';
import { normalizeCompanySlug } from '@shared/utils/company-slug.utils';
import { catchError, map, Observable, of } from 'rxjs';
import { IServerCompanyResult, ServerCompanyService } from './server-company.service';

/**
 * Só no SERVIDOR: escreve título, descrição e Open Graph da empresa no HTML do SSR, para as prévias de link
 * (WhatsApp, Instagram/Facebook, iMessage, Telegram...), que não executam JavaScript.
 * No navegador devolve na hora (não atrasa a navegação); lá o CompanyComponent chama o SeoService depois do /complete.
 * Nunca lança: erro em resolver vira "navigation error" e o SSR cairia no 404 do Express.
 */
export const companyPreviewResolver: ResolveFn<null> = (route: ActivatedRouteSnapshot): Observable<null> => {
  if (!isPlatformServer(inject(PLATFORM_ID))) return of(null);

  const slug: string | null = normalizeCompanySlug(route.paramMap.get('CompanySlug'));
  if (!slug) return of(null); // '/index.html', '/wp-login.php' e caminhos reservados não vão à API

  const seo: SeoService = inject(SeoService);
  // Nível B (plano 3, Fase 4b): o bloco de contato usa a mesma empresa, também com o tema público desligado
  const bootFallback: BootFallbackService = inject(BootFallbackService);
  // Passo 1.7 (opcional): 404 de verdade para slug inexistente. Sem o passo, apagar esta linha e o "else if" abaixo.
  const responseInit: ResponseInit | null = inject(RESPONSE_INIT, { optional: true });

  return inject(ServerCompanyService)
    .load(slug)
    .pipe(
      map((result: IServerCompanyResult): null => {
        if (result.company) {
          seo.applyCompany(result.company);
          bootFallback.setCompany(result.company);
        } else if (result.notFound && responseInit) responseInit.status = 404;
        return null;
      }),
      catchError(() => of(null))
    );
};
